import React from 'react';
import { format, eachDayOfInterval, startOfMonth, endOfMonth, getDay, startOfWeek, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';

interface OccupancyData {
  date: string;
  occupancyRate: number;
}

interface OccupancyHeatMapProps {
  data: OccupancyData[];
  year: number;
  month: number;
}

export default function OccupancyHeatMap({ data, year, month }: OccupancyHeatMapProps) {
  const startDate = startOfMonth(new Date(year, month));
  const endDate = endOfMonth(startDate);
  const startWeek = startOfWeek(startDate, { locale: fr });

  // Créer un tableau de toutes les dates du mois
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  // Créer une map des taux d'occupation par date
  const occupancyMap = new Map(
    data.map(item => [item.date, item.occupancyRate])
  );

  // Créer le calendrier avec les semaines
  const calendar = [];
  let currentWeek = [];
  let currentDate = startWeek;

  // Remplir les jours avant le début du mois
  while (currentDate < startDate) {
    currentWeek.push(null);
    currentDate = addDays(currentDate, 1);
  }

  // Remplir les jours du mois
  days.forEach(day => {
    if (currentWeek.length === 7) {
      calendar.push(currentWeek);
      currentWeek = [];
    }
    const dateStr = format(day, 'yyyy-MM-dd');
    currentWeek.push({
      date: day,
      occupancyRate: occupancyMap.get(dateStr) || 0
    });
  });

  // Remplir les jours restants de la dernière semaine
  while (currentWeek.length < 7) {
    currentWeek.push(null);
  }
  calendar.push(currentWeek);

  const getColorForOccupancy = (rate: number) => {
    if (rate === 0) return 'bg-gray-100';
    if (rate < 25) return 'bg-green-100';
    if (rate < 50) return 'bg-green-300';
    if (rate < 75) return 'bg-green-500';
    return 'bg-green-700';
  };

  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h3 className="text-lg font-semibold mb-4">
        Taux d'occupation {format(startDate, 'MMMM yyyy', { locale: fr })}
      </h3>
      
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekDays.map(day => (
          <div key={day} className="text-center text-sm font-medium text-gray-600">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {calendar.map((week, weekIndex) => (
          week.map((day, dayIndex) => {
            if (!day) {
              return <div key={`${weekIndex}-${dayIndex}`} className="aspect-square bg-gray-50" />;
            }

            return (
              <div
                key={`${weekIndex}-${dayIndex}`}
                className={`aspect-square ${getColorForOccupancy(day.occupancyRate)} relative group`}
              >
                <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-600">
                  {format(day.date, 'd')}
                </div>
                <div className="hidden group-hover:block absolute z-10 bg-gray-900 text-white text-xs rounded px-2 py-1 left-1/2 -translate-x-1/2 -top-8">
                  {format(day.date, 'PP', { locale: fr })}
                  <br />
                  {Math.round(day.occupancyRate)}% occupé
                </div>
              </div>
            );
          })
        ))}
      </div>

      {/* Légende */}
      <div className="mt-4 flex items-center justify-end gap-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-100 rounded"></div>
          <span className="text-sm text-gray-600">0%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-100 rounded"></div>
          <span className="text-sm text-gray-600">1-24%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-300 rounded"></div>
          <span className="text-sm text-gray-600">25-49%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span className="text-sm text-gray-600">50-74%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-700 rounded"></div>
          <span className="text-sm text-gray-600">75-100%</span>
        </div>
      </div>
    </div>
  );
}